import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatRank } from '../../utils/formatting';
import { getScoreColor } from '../../utils/coloring';
import { useChartState } from '../../hooks/charts';
import './Charts.css';

const HistoricalScoresChart = ({ 
  selectedLocation, 
  selectedCrime, 
  onChartOptionChange, 
  selectedChartOption, 
  selectedSource,
  compareMode
}) => {
  const [selectedChart, setSelectedChart] = useState(selectedChartOption);
  const {
    isMobile,
    windowWidth,
    isMenuOpen,
    setIsMenuOpen,
    isTouching,
    setIsTouching,
    menuRef,
    getDropdownLeftPosition
  } = useChartState();

  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  const crimeName = getCrimeDisplayName(selectedCrime, selectedSource);
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  useEffect(() => {
    if (!selectedChartOption) return;
    
    if (isCountyOrCity) {
      const currentPrefix = compareMode === 'state' ? stateAbbr : 'US';
      const hasCorrectPrefix = selectedChartOption.startsWith(currentPrefix);
      
      if (!hasCorrectPrefix) {
        if (selectedChartOption.includes('Composite') && selectedChartOption.includes('Rank')) {
          setSelectedChart(`${currentPrefix} ${locationType} Rank (Composite)`);
        } 
        else if (selectedChartOption.includes('Score (Non-Weighted)')) {
          setSelectedChart(`${currentPrefix} ${selectedCrime === 'all_crime' ? 'All ' : ''}${crimeName} Score (Non-Weighted)`);
        }
        else if (selectedChartOption.includes('Rank') && !selectedChartOption.includes('Composite')) {
          setSelectedChart(`${currentPrefix} ${locationType} Rank (Non-Weighted)`);
        }
        else if (selectedChartOption.includes('Score (Composite)')) {
          setSelectedChart(`${currentPrefix} KRIME Score (Composite)`);
        }
        else {
          const match = selectedChartOption.match(/^(US|[A-Z]{2})\s+(.*)$/);
          if (match) {
            setSelectedChart(`${currentPrefix} ${match[2]}`);
          } else {
            setSelectedChart(selectedChartOption);
          }
        }
      } else {
        setSelectedChart(selectedChartOption);
      }
    } else {
      setSelectedChart(selectedChartOption);
    }
  }, [selectedChartOption, compareMode, stateAbbr, isCountyOrCity, locationType, selectedCrime, crimeName]);

  const handleTouchStart = () => {
    setIsTouching(true);
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
  };

  if (isNation) {
    return (
      <div className="loading">
        Scores are not available for the entire nation
      </div>
    );
  }

  const chartOptions = [
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}KRIME Score (Composite)`,
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Rank (Composite)`,
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${selectedCrime === 'all_crime' ? 'All ' : ''}${crimeName} Score (Non-Weighted)`,
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Rank (Non-Weighted)`
  ];

  if (!selectedChart || !chartOptions.includes(selectedChart)) {
    return null;
  }

  const getTotalLocations = () => {
    if (!isCountyOrCity) {
      return data.current.total_locations || 0;
    }
    
    if (!data?.historical) {
      return compareMode === 'state' ? 
        (isCounty ? data.current.total_counties_state : data.current.total_cities_state) || data.current.total_locations : 
        (isCounty ? data.current.total_counties_national : data.current.total_cities_national) || data.current.total_locations;
    }
    
    let maxLocations = 0;
    
    data.historical.forEach(yearData => {
      const totalLocations = compareMode === 'state' ? 
        (isCounty ? yearData.total_counties_state : yearData.total_cities_state) : 
        (isCounty ? yearData.total_counties_national : yearData.total_cities_national);
      
      if (totalLocations && totalLocations > maxLocations) {
        maxLocations = totalLocations;
      }
    });
    
    if (maxLocations === 0 && data.current) {
      return compareMode === 'state' ? 
        (isCounty ? data.current.total_counties_state : data.current.total_cities_state) || data.current.total_locations : 
        (isCounty ? data.current.total_counties_national : data.current.total_cities_national) || data.current.total_locations;
    }
    
    return maxLocations;
  };

  const extractNumber = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }

    const rawData = data.historical
      .sort((a, b) => a.year - b.year)
      .map(yearData => {
        const baseScoreField = `${selectedCrime.toLowerCase()}_score`;
        
        const scoreField = isCountyOrCity
          ? compareMode === 'state'
            ? `${selectedCrime.toLowerCase()}_state_score`
            : `${selectedCrime.toLowerCase()}_national_score`
          : baseScoreField;
        
        const score = yearData[scoreField];
        
        if (score === null || score === undefined) return null;

        const rankField = isCountyOrCity
          ? compareMode === 'state'
            ? `${selectedCrime.toLowerCase()}_state_score_rank`
            : `${selectedCrime.toLowerCase()}_national_score_rank`
          : `${selectedCrime.toLowerCase()}_score_rank`;
        
        
        const compositeScoreField = isCountyOrCity
          ? compareMode === 'state'
            ? 'state_composite_score'
            : 'national_composite_score'
          : 'composite_score';
        
        const compositeRankField = isCountyOrCity
          ? compareMode === 'state'
            ? 'state_composite_score_rank'
            : 'national_composite_score_rank'
          : 'composite_score_rank';

        return {
          year: yearData.year,
          score,
          compositeScore: yearData[compositeScoreField],
          compositeScoreRank: yearData[compositeRankField],
          compositeScoreRankValue: extractNumber(yearData[compositeRankField]),
          rank: yearData[rankField],
          rankValue: extractNumber(yearData[rankField])
        };
      })
      .filter(row => row !== null);

    if (rawData.length === 0) return rawData;

    const availableYears = rawData.map(item => item.year);
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    
    const dataMap = new Map();
    rawData.forEach(item => {
      dataMap.set(item.year, item);
    });
    
    const completeData = [];
    for (let year = minYear; year <= maxYear; year++) {
      if (dataMap.has(year)) {
        completeData.push(dataMap.get(year));
      } else {
        completeData.push({
          year,
          score: null,
          compositeScore: null,
          compositeScoreRank: null,
          compositeScoreRankValue: null,
          rank: null,
          rankValue: null
        });
      }
    }

    return completeData;
  };

  const chartData = transformData();

  const getMaxValue = () => {
    if (selectedChart.includes('Rank')) {
      return getTotalLocations();
    } else {
      return 100;
    }
  };

  const getTicks = () => {
    if (selectedChart.includes('Rank')) {
      return [getTotalLocations(), 
              Math.ceil(getTotalLocations()*3/4), 
              Math.ceil(getTotalLocations()/2), 
              Math.ceil(getTotalLocations()/4), 
              1];
    } else {
      return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    }
  };

  const getCustomTicks = () => {
    if (!chartData || chartData.length === 0) return [];
    return chartData
      .filter(item => item.score !== null || item.compositeScore !== null)
      .map(item => item.year)
      .sort((a, b) => a - b);
  };

  const getYAxisDomain = () => {
    if (selectedChart.includes('Rank')) {
      const buffer = getTotalLocations() * 0.05;
      return [1 - buffer, getTotalLocations() + buffer];
    } else {
      return [-5, 105];
    }
  };

  const renderCustomLegend = () => {
    const renderLegendIcon = () => {
      const lineStrokeWidth = 2;
      const dotRadius = 4; 

      if (selectedChart.includes('Rank')) {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <svg width="12" height="12">
              <circle
                cx="6"
                cy="6"
                r={dotRadius}
                fill="rgba(26, 44, 78, 0.6)"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
              />
            </svg>
          </div>
        );
      } else {
        return (
          <svg width={isMobile ? '60' : '80'} height="12">
            <line
              x1="0"
              y1="6"
              x2={isMobile ? '60' : '80'}
              y2="6"
              stroke="rgba(26, 44, 78, 0.6)"
              strokeWidth={lineStrokeWidth}
            />
            <circle
              cx={isMobile ? '30' : '40'}
              cy="6"
              r={dotRadius}
              fill="rgba(26, 44, 78, 0.6)"
              stroke="rgba(26, 44, 78, 0.6)"
              strokeWidth={2}
            />
          </svg>
        );
      }
    };

    return (
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0 10px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderLegendIcon()}
          <div 
            ref={menuRef}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              gap: '4px',
              position: 'relative'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>
              {selectedChart.replace('KRIME Score', 'KRIME Score™')}
            </span>
            <ChevronDown className="chart-legend-chevron" style={{ width: '16px', height: '16px' }} />
          
            {isMenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: getDropdownLeftPosition(selectedChart, true),
                backgroundColor: 'white',
                border: '1px solid var(--silver)',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: 'max-content',
                marginTop: '4px',
                whiteSpace: 'nowrap'
              }}>
                {chartOptions.map((option, index) => (
                  <div
                    key={option}
                    style={{
                      padding: '8px 12px',
                      cursor: selectedChart === option ? 'not-allowed' : 'pointer',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: 600,
                      textAlign: 'left',
                      backgroundColor: selectedChart === option ? 'var(--light-gray)' : 'transparent',
                      borderBottom: index < chartOptions.length - 1 ? '1px solid var(--border-color)' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      boxSizing: 'border-box',
                      height: '31px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedChart !== option) {
                        e.currentTarget.style.backgroundColor = 'var(--silver)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedChart !== option) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    onClick={(e) => {
                      if (selectedChart === option) {
                        e.stopPropagation();
                        return;
                      }
                      e.stopPropagation();
                      setSelectedChart(option);
                      onChartOptionChange(option);
                      setIsMenuOpen(false);
                    }}
                  >
                    <span style={{ opacity: selectedChart === option ? 0.5 : 1 }}>
                      {option.replace('KRIME Score', 'KRIME Score™')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCommonChartComponents = () => (
    <>
      <CartesianGrid stroke="var(--border-color)" strokeDasharray="none" />
      <XAxis 
        dataKey="year"
        axisLine={{ stroke: 'var(--border-color)' }}
        tickLine={{ stroke: 'var(--border-color)' }}
        tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, fill: 'black' }}
        ticks={getCustomTicks()}
        domain={['dataMin', 'dataMax']}
        type="number"
        scale="linear"
        interval={0}
        allowDecimals={false}
        height={isMobile ? 20 : 30}
        tickFormatter={(value, index) => {
          const availableYears = getCustomTicks();
          const currentIndex = availableYears.indexOf(value);
          
          if (currentIndex === -1) return '';
          
          const totalYears = availableYears.length;
          
          let maxLabels;
          if (windowWidth <= 480) {
            maxLabels = 5; 
          } else if (windowWidth <= 767) {
            maxLabels = 7; 
          } else if (windowWidth <= 1023) {
            maxLabels = 9; 
          } else if (windowWidth <= 1300) {
            maxLabels = 7; 
          } else {
            maxLabels = 11; 
          }
          
          let minYearGap = 1;
          if (totalYears > 5) {
            if (windowWidth <= 480) {
              minYearGap = 3; 
            } else {
              minYearGap = 2; 
            }
          }
          
          if (currentIndex === 0 || currentIndex === totalYears - 1) {
            return value;
          }
          
          if (maxLabels <= 2) {
            return '';
          }
          
          const selectedIndices = [0, totalYears - 1];
          
          const additionalLabels = maxLabels - 2;
          for (let i = 1; i <= additionalLabels; i++) {
            const index = Math.round(((totalYears - 1) * i) / (additionalLabels + 1));
            
            const yearValue = availableYears[index];
            let meetsMinGap = true;
            
            for (const selectedIndex of selectedIndices) {
              const selectedYear = availableYears[selectedIndex];
              if (Math.abs(yearValue - selectedYear) < minYearGap) {
                meetsMinGap = false;
                break;
              }
            }
            
            if (meetsMinGap) {
              selectedIndices.push(index);
            }
          }
          
          return selectedIndices.includes(currentIndex) ? value : '';
        }}
      />
      <YAxis
        domain={getYAxisDomain()}
        ticks={getTicks(getMaxValue())}
        axisLine={{ stroke: 'var(--border-color)' }}
        tickLine={{ stroke: 'var(--border-color)' }}
        interval={isMobile ? 0 : "preserveStartEnd"}
        tickFormatter={(value) => {
          if (value === 0) return '0';
          if (selectedChart.includes('Rank')) {
            return formatRank(value);
          } else {
            return formatNumber(value);
          }
        }}
        tick={props => {
          const { x, y, payload, height } = props;
          const domain = getYAxisDomain();
          const isBottomTick = payload.value === domain[0];
          const adjustedY = isBottomTick ? y - 5 : y;
          
          const totalHeight = height * (100 / 110);
          const spacing = totalHeight / 10;
          const offsetY = height * (5 / 110); 
          
          const shouldColorBackground = 
            selectedChart.includes('Score (Composite)') || 
            selectedChart.includes('Score (Non-Weighted)');
          
          const backgroundColor = shouldColorBackground ? getScoreColor(payload.value) : 'transparent';

          const rightMargin = isMobile ? 30 : 40;
          
          return (
            <g>
              {shouldColorBackground && payload.value >= 0 && payload.value < 100 && (
                <rect
                  x={x + 8}
                  y={offsetY + ((9 - payload.value / 10) * spacing)}
                  width={`calc(100% - ${x + 8 + rightMargin}px)`}
                  height={spacing}
                  fill={backgroundColor}
                  fillOpacity={0.4}
                />
              )}
              <text
                x={x}
                y={adjustedY}
                textAnchor="end"
                fill="black"
                fontSize={isMobile ? 12 : 14}
                fontWeight={400}
                dy={4}
              >
                {props.tickFormatter(payload.value)}
              </text>
            </g>
          );
        }}
      />
      <Tooltip
        active={isMobile ? isTouching : undefined}
        content={({ active, payload, label }) => {
          if (active && payload && payload.length) {
            const data = payload[0].payload;
            
            const getRowStyle = (type) => ({
              display: 'flex', 
              justifyContent: 'space-between', 
              gap: '24px',
              backgroundColor: (
                (selectedChart.includes('Score (Composite)') && type === 'Score (Composite)') ||  
                (selectedChart.includes('Composite') && selectedChart.includes('Rank') && type === 'Composite Rank') ||
                (selectedChart.includes('Score (Non-Weighted)') && type === 'Score (Non-Weighted)') ||
                (selectedChart.includes('Rank') && !selectedChart.includes('Composite') && type === 'Non-Weighted Rank')
              ) ? '#fef9c3' : 'transparent',
              padding: '2px 4px',
              margin: '0 -4px'
            });
            
            if (data.score !== null || data.compositeScore !== null) {
              return (
                <div className="custom-tooltip" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  border: '1px solid var(--silver)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    color: 'black',
                    textAlign: 'left',
                    paddingBottom: '2px',
                  }}>
                    {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                  </div>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 400,
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--silver)',
                    paddingBottom: '8px'
                  }}>
                    {data.year}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', display: 'grid', gap: '4px' }}>
                    <div style={getRowStyle('Score (Composite)')}>
                      <span style={{ fontWeight: 600 }}>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') + ' ' : ''}KRIME Score&trade; (Composite):</span>
                      <span style={{ color: getScoreColor(data.compositeScore) }}><strong>{formatNumber(data.compositeScore, true)}</strong></span>
                    </div>
                    <div style={getRowStyle('Composite Rank')}>
                      <span style={{ fontWeight: 600 }}>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') + ' ' : ''}{locationType} Rank (Composite):</span>
                      <span>{formatRank(data.compositeScoreRank)}</span>
                    </div>
                    <div style={getRowStyle('Score (Non-Weighted)')}>
                      <span style={{ fontWeight: 600 }}>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') + ' ' : ''}{selectedCrime === 'all_crime' ? 'All ' : ''}{crimeName} Score (Non-Weighted):</span>
                      <span style={{ color: getScoreColor(data.score) }}><strong><em>{formatNumber(data.score, true)}</em></strong></span>
                    </div>
                    <div style={getRowStyle('Non-Weighted Rank')}>
                      <span style={{ fontWeight: 600 }}>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') + ' ' : ''}{locationType} Rank (Non-Weighted):</span>
                      <span><em>{formatRank(data.rank)}</em></span>
                    </div>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="custom-tooltip" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  border: '1px solid var(--silver)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    color: 'black',
                    textAlign: 'left',
                    paddingBottom: '2px',
                  }}>
                    {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                  </div>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 400,
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--silver)',
                    paddingBottom: '8px'
                  }}>
                    {data.year}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', padding: '20px' }}>
                    No data available
                  </div>
                </div>
              );
            }
          }
          return null;
        }}
      />
    </>
  );

  const getValue = (data) => {
    if (selectedChart.includes('Score (Composite)')) {
      return data.compositeScore;
    } else if (selectedChart.includes('Composite') && selectedChart.includes('Rank')) {
      return data.compositeScoreRankValue;
    } else if (selectedChart.includes('Score (Non-Weighted)')) {
      return data.score;
    } else if (selectedChart.includes('Rank') && !selectedChart.includes('Composite')) {
      return data.rankValue;
    }
    return 0;
  };
  
  return (
    <div className={`chart-container ${isMenuOpen ? 'active' : ''}`}>
      {renderCustomLegend()}
      <div 
        className="chart-content"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? -10 : 30, bottom: 0 }}
          >
            {renderCommonChartComponents()}
            {(() => {
              const availableYears = chartData.filter(item => item.score !== null || item.compositeScore !== null).sort((a, b) => a.year - b.year);
              const gapSegments = [];
              
              for (let i = 0; i < availableYears.length - 1; i++) {
                const currentPoint = availableYears[i];
                const nextPoint = availableYears[i + 1];
                
                if (nextPoint.year - currentPoint.year > 1) {
                  gapSegments.push(
                    <Line
                      key={`gap-${currentPoint.year}-${nextPoint.year}`}
                      type="monotone"
                      dataKey={(data) => {
                        if (data.year === currentPoint.year || data.year === nextPoint.year) {
                          return getValue(data);
                        }
                        return null;
                      }}
                      stroke={selectedChart.includes('Rank') ? 'transparent' : 'var(--border-color)'}
                      strokeWidth={2}
                      strokeDasharray="none"
                      dot={false}
                      activeDot={false}
                      connectNulls={true}
                    />
                  );
                }
              }
              
              return gapSegments;
            })()}
            <Line 
              dataKey={(data) => (data.score === null && data.compositeScore === null) ? 0 : getValue(data)}
              stroke="transparent"
              strokeWidth={0}
              dot={false}
              activeDot={false}
            />
            <Line
              type="monotone"
              dataKey={(data) => getValue(data)}
              stroke={selectedChart.includes('Rank') ? 'none' : 'rgba(26, 44, 78, 0.6)'}
              strokeWidth={2}
              strokeDasharray="none"
              dot={selectedChart.includes('Rank') ? {
                r: 4,
                fill: 'var(--navy)',
                fillOpacity: 0.6,
                stroke: 'rgba(26, 44, 78, 0.6)'
              } : false}
              activeDot={isMobile ? (isTouching ? (selectedChart.includes('Rank') ? {
                r: 4,
                fill: 'var(--navy)',
                fillOpacity: 0.9,
                stroke: 'rgba(26, 44, 78, 0.9)'
              } : { 
                r: 4, 
                stroke: 'rgba(26, 44, 78, 0.6)' 
              }) : false) : (selectedChart.includes('Rank') ? {
                r: 4,
                fill: 'var(--navy)',
                fillOpacity: 0.6,
                stroke: 'rgba(26, 44, 78, 0.6)'
              } : { 
                r: 4, 
                stroke: 'rgba(26, 44, 78, 0.6)' 
              })}
              animationDuration={selectedChart.includes('Rank') ? 0 : 1000}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HistoricalScoresChart;